import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ParticipantService } from '../../../services/participant.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { forkJoin } from 'rxjs';

interface ParticipantProfile {
  participant: any;
  financialSummary: any;
  enrollments: any[];
  corporateEnrollments: any[];
  incomeReceipts: any[];
  excessPayments: any[];
  refundVouchers: any[];
  creditNotes: any[];
  debitNotes: any[];
}

@Component({
  selector: 'app-profile-participant',
  templateUrl: './profile-participant.component.html',
  styleUrl: './profile-participant.component.scss'
})
export class ProfileParticipantComponent implements OnInit {

  participantId: number = 0;
  profile: ParticipantProfile | null = null;
  activeTab: string = 'overview';
  
  // Estados de carga
  isLoading: boolean = false;

  // Vista de inscripciones
  enrollmentsView: 'individual' | 'corporate' = 'individual';
  individualEnrollments: any[] = [];
  corporateEnrollments: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private participantService: ParticipantService,
    private enrollmentService: EnrollmentService,
    private loadingService: LoadingService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.participantId = +id;
        this.loadParticipantProfile();
      } else {
        this.toastService.showToast('error', 'ID del participante no válido');
        this.router.navigate(['/participants']);
      }
    });
  }

  private loadParticipantProfile(): void {
    if (this.participantId <= 0) return;
    
    this.isLoading = true;
    this.loadingService.show();
    
    this.participantService.getProfile(this.participantId).subscribe({
      next: (response) => {
        console.log('📋 Profile response:', response);
        // Manejar respuesta que puede venir envuelta en 'data' o directamente
        this.profile = response?.data || response;
        console.log('📋 Profile asignado:', this.profile);
        console.log('📋 Enrollments count:', this.profile?.enrollments?.length || 0);
        
        // Normalizar paymentPlans (puede venir como PaymentPlans desde el backend)
        if (this.profile?.enrollments && this.profile.enrollments.length > 0) {
          this.profile.enrollments.forEach((enrollment: any, index: number) => {
            console.log(`📋 Enrollment ${index} (ID: ${enrollment.id}):`, {
              hasPaymentPlans: !!enrollment.paymentPlans,
              paymentPlansLength: enrollment.paymentPlans?.length || 0,
              paymentPlansType: typeof enrollment.paymentPlans,
              paymentPlansIsArray: Array.isArray(enrollment.paymentPlans),
              hasPaymentPlansPascal: !!enrollment.PaymentPlans,
              PaymentPlansLength: enrollment.PaymentPlans?.length || 0
            });
            
            // Si viene como PaymentPlans (PascalCase), normalizar a paymentPlans (camelCase)
            if (!enrollment.paymentPlans && enrollment.PaymentPlans) {
              enrollment.paymentPlans = enrollment.PaymentPlans;
              console.log(`✅ Normalizado PaymentPlans a paymentPlans para enrollment ${enrollment.id}`);
            }
            // Asegurar que siempre sea un array
            if (!Array.isArray(enrollment.paymentPlans)) {
              if (enrollment.paymentPlans) {
                console.warn(`⚠️ paymentPlans no es un array para enrollment ${enrollment.id}:`, typeof enrollment.paymentPlans);
              }
              enrollment.paymentPlans = Array.isArray(enrollment.paymentPlans) ? enrollment.paymentPlans : [];
            }
            
            console.log(`📋 Enrollment ${enrollment.id} después de normalización:`, {
              paymentPlansLength: enrollment.paymentPlans?.length || 0,
              paymentPlansIsArray: Array.isArray(enrollment.paymentPlans)
            });
          });

          const enrollmentsWithoutPlans = this.profile.enrollments.filter((e: any) => 
            !e.paymentPlans || !Array.isArray(e.paymentPlans) || e.paymentPlans.length === 0
          );
          
          console.log(`📋 Enrollments sin paymentPlans: ${enrollmentsWithoutPlans.length} de ${this.profile.enrollments.length}`);
          
          // Si hay enrollments sin paymentPlans, cargarlos individualmente
          if (enrollmentsWithoutPlans.length > 0) {
            console.log(`📋 Cargando paymentPlans para ${enrollmentsWithoutPlans.length} inscripciones...`);
            this.loadEnrollmentDetails(enrollmentsWithoutPlans);
          } else {
            console.log('✅ Todos los enrollments tienen paymentPlans, procediendo a mostrar...');
            this.splitEnrollmentsByType();
            this.isLoading = false;
            this.loadingService.hide();
          }
        } else {
          this.splitEnrollmentsByType();
          this.isLoading = false;
          this.loadingService.hide();
        }
      },
      error: (error) => {
        console.error('Error loading participant profile:', error);
        this.toastService.showToast('error', 'Error cargando el perfil del participante');
        this.isLoading = false;
        this.loadingService.hide();
      }
    });
  }

  /**
   * Carga los detalles completos (incluyendo paymentPlans) de las inscripciones que no los tienen
   */
  private loadEnrollmentDetails(enrollments: any[]): void {
    const enrollmentRequests = enrollments.map((enrollment: any) => 
      this.enrollmentService.getById(enrollment.id)
    );

    forkJoin(enrollmentRequests).subscribe({
      next: (responses) => {
        responses.forEach((response: any, index: number) => {
          const enrollmentId = enrollments[index].id;
          const enrollmentIndex = this.profile!.enrollments.findIndex((e: any) => e.id === enrollmentId);
          
          if (enrollmentIndex !== -1 && response?.data) {
            // Normalizar paymentPlans (puede venir como PaymentPlans)
            const paymentPlans = response.data.paymentPlans || response.data.PaymentPlans || [];
            this.profile!.enrollments[enrollmentIndex].paymentPlans = Array.isArray(paymentPlans) ? paymentPlans : [];
            console.log(`✅ PaymentPlans cargados para enrollment ${enrollmentId}:`, 
              this.profile!.enrollments[enrollmentIndex].paymentPlans?.length || 0);
          }
        });
        
        this.splitEnrollmentsByType();
        this.isLoading = false;
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading enrollment details:', error);
        // Continuar aunque falle, mostrar lo que tenemos
        this.splitEnrollmentsByType();
        this.isLoading = false;
        this.loadingService.hide();
      }
    });
  }

  private splitEnrollmentsByType(): void {
    if (!this.profile) {
      this.individualEnrollments = [];
      this.corporateEnrollments = [];
      return;
    }

    // Las inscripciones individuales vienen del campo 'enrollments'
    const individualEnrollments = Array.isArray(this.profile.enrollments) ? this.profile.enrollments : [];
    this.individualEnrollments = individualEnrollments.filter(e => this.isIndividualEnrollment(e));

    // Las inscripciones corporativas vienen del campo 'corporateEnrollments'
    const corporateEnrollments = Array.isArray(this.profile.corporateEnrollments) ? this.profile.corporateEnrollments : [];
    this.corporateEnrollments = corporateEnrollments;
  }

  private isIndividualEnrollment(enrollment: any): boolean {
    const type = (enrollment?.enrollmentType ?? '').toString().toLowerCase();
    // soportar enum como string o numérico
    return type === 'individual' || type === '0' || type === '';
  }

  private isCorporateEnrollment(enrollment: any): boolean {
    const type = (enrollment?.enrollmentType ?? '').toString().toLowerCase();
    return type === 'corporate' || type === '1';
  }

  // Filtra los planes de pago únicamente del participante (excluye los de empresa)
  getParticipantPlans(enrollment: any): any[] {
    const plans = Array.isArray(enrollment?.paymentPlans) ? enrollment.paymentPlans : [];
    // Aceptamos payerType como string o numérico; asumimos 'PARTICIPANT' o 2 para el participante
    return plans.filter((pp: any) => {
      const payer = (pp?.payerType ?? '').toString().toUpperCase();
      return payer === 'PARTICIPANT' || payer === '2';
    });
  }

  switchEnrollmentsView(view: 'individual' | 'corporate'): void {
    this.enrollmentsView = view;
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  isActiveTab(tab: string): boolean {
    return this.activeTab === tab;
  }

  goToEdit(): void {
    this.router.navigate(['/dashboard/participants/new-participant', this.participantId]);
  }

  goBack(): void {
    this.router.navigate(['/participants']);
  }

  // Métodos utilitarios para el template
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    }).format(amount);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('es-CO');
  }

  getStatusClass(status: string | number | null | undefined): string {
    // Manejar null o undefined
    if (status == null) {
      return 'bg-gray-100 text-gray-800';
    }

    // Convertir a string si es número
    let statusStr: string;
    if (typeof status === 'number') {
      // Mapear números de PaymentStatus enum a strings (según backend: PENDING=1, PAID=2, OVERDUE=3, PARTIALLY_PAID=4, LINK_GENERATED=5)
      const statusMap: { [key: number]: string } = {
        0: 'pending',  // Valor por defecto si viene 0
        1: 'pending',
        2: 'paid',
        3: 'overdue',
        4: 'partially_paid',
        5: 'link_generated'
      };
      statusStr = statusMap[status] || 'pending';
    } else {
      statusStr = String(status);
    }

    switch (statusStr.toLowerCase()) {
      case 'completed':
      case 'completado':
      case 'approved':
      case 'aprobado':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'cancelado':
      case 'rejected':
      case 'rechazado':
        return 'bg-red-100 text-red-800';
      case 'processing':
      case 'en proceso':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'partially_paid':
      case 'partially paid':
        return 'bg-blue-100 text-blue-800';
      case 'link_generated':
      case 'link generated':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getPaymentStatusClass(status: string | number | null | undefined): string {
    return this.getStatusClass(status);
  }

  getPaymentStatusLabel(status: string | number | null | undefined): string {
    if (status == null) {
      return 'Desconocido';
    }

    // Convertir a string si es número
    let statusStr: string;
    if (typeof status === 'number') {
      const statusMap: { [key: number]: string } = {
        0: 'Pendiente',
        1: 'Pendiente',
        2: 'Pagado',
        3: 'Vencido',
        4: 'Parcialmente Pagado',
        5: 'Link Generado'
      };
      statusStr = statusMap[status] || 'Pendiente';
    } else {
      const stringMap: { [key: string]: string } = {
        'PENDING': 'Pendiente',
        'PAID': 'Pagado',
        'OVERDUE': 'Vencido',
        'PARTIALLY_PAID': 'Parcialmente Pagado',
        'LINK_GENERATED': 'Link Generado'
      };
      statusStr = stringMap[status.toUpperCase()] || status;
    }

    return statusStr;
  }
}
